// src\themes\lino\elements\Header\HeaderMegaMenu.tsx
'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { ArrowUpLeft, ChevronDown } from 'lucide-react';

import { MenuItem } from '@/core/types/menuApi';
import { useMegaMenuStore } from '@/stores/megaMenuStore';
import { normalizeLink } from '@/core/lib/utils';

// تاخیر قبل از بسته‌شدن منو (بهش اجازه می‌ده ماوس بین آیتم و پنل جابه‌جا بشه
// بدون اینکه پنل زودتر از موعد بسته بشه)
const CLOSE_DELAY = 350;

export default function HeaderMegaMenu({ items }: { items: MenuItem[] }) {
  const [openId, setOpenId] = useState<number | null>(null);
  const [hoveredChildId, setHoveredChildId] = useState<number | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeItem = items.find(i => i.id === openId);
  const setMegaMenuOpen = useMegaMenuStore(s => s.setIsOpen);

  useEffect(() => {
    setMegaMenuOpen(openId !== null);
  }, [openId, setMegaMenuOpen]);

  const clearCloseTimer = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const scheduleClose = () => {
    clearCloseTimer();
    closeTimer.current = setTimeout(() => setOpenId(null), CLOSE_DELAY);
  };

  const openMenu = (id: number | null) => {
    clearCloseTimer();
    setOpenId(id);
    setHoveredChildId(null);
  };

  useEffect(() => () => {
    clearCloseTimer();
    setMegaMenuOpen(false);
  }, []);

  return (
    <nav
      className="hidden lg:flex items-center gap-1 h-full"
      onMouseLeave={scheduleClose}
    >
      {items.map(item => {
        const hasChildren = !!item.children?.length;
        const isOpen = openId === item.id;

        return (
          <div
            key={item.id}
            className="relative h-full flex items-center"
            onMouseEnter={() => openMenu(hasChildren ? item.id : null)}
          >
            <Link
              href={normalizeLink(item.link) || '#'}
              className={`flex items-center gap-1 px-3 py-2 text-[13px] tracking-wide whitespace-nowrap underline decoration-1 underline-offset-[6px] transition-colors ${isOpen
                ? 'text-primary-900 decoration-primary-900 group-data-[transparent=true]:text-primary-0 group-data-[transparent=true]:decoration-primary-0'
                : 'text-primary-800 decoration-transparent hover:decoration-primary-900 group-data-[transparent=true]:text-primary-0/90 group-data-[transparent=true]:hover:text-primary-0 group-data-[transparent=true]:hover:decoration-primary-0'
                }`}
            >
              {item.title}
              {hasChildren && (
                <ChevronDown
                  className={`size-3 stroke-[1.75] shrink-0 opacity-70 no-underline transition-transform duration-300 ${isOpen ? 'rotate-180' : ''
                    }`}
                />
              )}
            </Link>
          </div>
        );
      })}

      {/* پنل کشویی — کلید ثابته (نه activeItem.id)، پس فقط با باز/بسته شدنِ
          کلی منو مونت/آنمونت می‌شه؛ با عوض شدن آیتم دیگه از نو ساخته نمی‌شه */}
      <AnimatePresence>
        {activeItem && (
          <motion.div
            key="mega-menu-panel"
            initial={{ opacity: 0, y: -16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="fixed left-0 right-0 top-16 z-40"
            onMouseEnter={clearCloseTimer}
            onMouseLeave={scheduleClose}
          >
            {/* سایه رو رو یه لایه‌ی بیرونیِ بدون clip-path/overflow-hidden نگه
                می‌داریم؛ چون اگه رو همون باکس (یا هر والدی) که clip-path/overflow
                داره بذاریمش، سایه‌اش کلاً کلیپ می‌شه و دیده نمی‌شه (باگ معروف CSS) */}
            <div className="rounded-b-[1.75rem] shadow-[0_18px_20px_-18px_rgba(0,0,0,0.35)]">
              {/* پرده‌ی کشویی: این لایه clip-path داره تا محتوا از بالا باز بشه؛
                  چون داخل باکسِ سایه‌س نه خودش، سایه‌ی بیرونی دست‌نخورده می‌مونه */}
              <motion.div
                initial={{ clipPath: 'inset(0% 0% 100% 0%)' }}
                animate={{ clipPath: 'inset(0% 0% 0% 0%)' }}
                exit={{ clipPath: 'inset(0% 0% 100% 0%)' }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className="bg-primary-50 rounded-b-[1.75rem] px-5 sm:px-8 lg:px-12 pt-10 pb-8 overflow-hidden"
              >
                {/* محتوای داخلی جدا انیمیشن می‌خوره: با تعویض آیتم، فقط این
                    بخش کراس‌فید می‌شه، پنل بیرونی سرجاش باز می‌مونه */}
                <AnimatePresence mode="wait">
                  {activeItem && (
                    <motion.div
                      key={activeItem.id}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                      className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-x-8 gap-y-6 items-start"
                    >
                      {/* کارت تصویری — جمع‌تر و کوچیک‌تر از قبل */}
                      {(() => {
                        const hoveredChild = hoveredChildId != null
                          ? activeItem.children?.find(c => c.id === hoveredChildId && c.imageUrl)
                          : undefined;
                        const withImage = hoveredChild ?? activeItem.children?.find(c => c.imageUrl);
                        if (!withImage) return null;
                        return (
                          <Link
                            key={withImage.id}
                            href={normalizeLink(withImage.link || activeItem.link) || '#'}
                            className="relative rounded-[0.5rem] overflow-hidden aspect-[4/3] group block"
                          >
                            <Image
                              src={withImage.imageUrl!}
                              alt={withImage.imageName || activeItem.title}
                              fill
                              sizes="220px"
                              className="object-cover transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-primary-950/50 to-transparent" />
                            <div className="absolute bottom-3 right-3 left-3 flex items-center justify-between">
                              <span className="text-primary-0 text-xs">{withImage.title}</span>
                              <span className="size-7 rounded-full bg-primary-0/90 flex items-center justify-center">
                                <ArrowUpLeft className="size-3 text-primary-900" />
                              </span>
                            </div>
                          </Link>
                        );
                      })()}

                      {/* گرید لینک‌ها */}
                      <div className="grid grid-cols-2 gap-x-8 gap-y-3 content-start">
                        {activeItem.children?.map(child => (
                          <Link
                            key={child.id}
                            href={normalizeLink(child.link) || '#'}
                            onMouseEnter={() => setHoveredChildId(child.id)}
                            onClick={() => {
                              clearCloseTimer();
                              setOpenId(null);
                            }}
                            className="text-[13px] text-primary-700 hover:text-primary-900 transition-colors py-1.5 border-b border-primary-100/80"
                          >
                            {child.title}
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}